import React, { useState, useEffect, useCallback } from "react";
import { Table, Form, Row, Col, Card, Button, Spinner, Alert, Badge, Pagination } from "react-bootstrap";
import { FaBookOpen, FaFileExcel, FaDownload, FaFilter, FaSearch, FaHistory } from "react-icons/fa";
import * as XLSX from "xlsx";
import firebaseApp from "../../../firebase/credenciales";
import { getFirestore, collection, query, where, getDocs, limit, orderBy } from "firebase/firestore";
import { periodosList, periodoActual, toNum } from "../utils";

const db = getFirestore(firebaseApp);

const BTN = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };

const LibroDiario = ({ usuario, onSelectCuo }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [periodo, setPeriodo] = useState(periodoActual());
  const [fecha, setFecha] = useState("");
  const [cuo, setCuo] = useState("");
  const [cuenta, setCuenta] = useState("");
  const [origen, setOrigen] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [asientos, setAsientos] = useState([]);
  const [filteredAsientos, setFilteredAsientos] = useState([]);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const cargarDatos = useCallback(async () => {
    if (!rucEmpresa) return;
    try {
      setLoading(true);
      setError("");
      const q = query(
        collection(db, "gco_asientos"),
        where("ruc_empresa", "==", rucEmpresa),
        orderBy("fecha", "desc"),
        limit(500)
      );
      const snap = await getDocs(q);
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAsientos(data);
    } catch (e) {
      console.error(e);
      setError("No se pudieron cargar los asientos desde gco_asientos.");
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Filtrado local para filtros cruzados avanzados
  useEffect(() => {
    let result = asientos;
    
    if (periodo) {
      result = result.filter(a => String(a.fecha).startsWith(periodo));
    }
    if (fecha) {
      result = result.filter(a => a.fecha === fecha);
    }
    if (cuo) {
      result = result.filter(a => String(a.cuo || "").toLowerCase().includes(cuo.toLowerCase()));
    }
    if (origen) {
      result = result.filter(a => String(a.origen || "").toUpperCase() === origen.toUpperCase());
    }
    if (cuenta) {
      result = result.filter(a => 
        (a.debe || []).some(d => String(d.cuenta).startsWith(cuenta)) ||
        (a.haber || []).some(h => String(h.cuenta).startsWith(cuenta))
      );
    }

    setFilteredAsientos(result);
    setCurrentPage(1);
  }, [asientos, periodo, fecha, cuo, cuenta, origen]);

  // Paginación local
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAsientos.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAsientos.length / itemsPerPage);

  const exportarExcel = () => {
    const rows = [];
    filteredAsientos.forEach(a => {
      const maxLines = Math.max(a.debe?.length || 0, a.haber?.length || 0);
      for (let i = 0; i < maxLines; i++) {
        const d = a.debe?.[i] || {};
        const h = a.haber?.[i] || {};
        rows.push({
          Periodo: periodo,
          Fecha: a.fecha,
          CUO: a.cuo || "—",
          Glosa: a.glosa,
          Origen: a.origen || "GENERICO",
          "Cuenta Debe": d.cuenta || "",
          "Monto Debe": d.monto ? toNum(d.monto) : "",
          "Cuenta Haber": h.cuenta || "",
          "Monto Haber": h.monto ? toNum(h.monto) : "",
          Moneda: a.moneda || "PEN",
          Estado: a.estado || "REGISTRADO"
        });
      }
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Libro_Diario");
    XLSX.writeFile(wb, `Libro_Diario_${rucEmpresa}_${periodo}.xlsx`);
  };

  const exportarTxtPle = () => {
    const nombre = `LE${rucEmpresa}${periodo.replace("-", "")}000501001111.txt`;
    let lineCount = 1;
    const lines = [];

    filteredAsientos.forEach(a => {
      const maxLines = Math.max(a.debe?.length || 0, a.haber?.length || 0);
      for (let i = 0; i < maxLines; i++) {
        const d = a.debe?.[i] || {};
        const h = a.haber?.[i] || {};
        const cuentaActiva = d.cuenta || h.cuenta || "";
        const debeM = d.monto ? toNum(d.monto).toFixed(2) : "0.00";
        const haberM = h.monto ? toNum(h.monto).toFixed(2) : "0.00";
        
        // Formato PLE 5.1 Libro Diario Simplificado / General
        const line = `${periodo.replace("-", "")}00|${a.cuo || "—"}|M0000001|${cuentaActiva}|1|1|${debeM}|${haberM}|01|${a.fecha.replace(/-/g, "")}|${a.glosa.slice(0, 60)}|1|`;
        lines.push(line);
        lineCount++;
      }
    });

    const url = URL.createObjectURL(new Blob([lines.join("\r\n")], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = nombre;
    link.click();
    URL.revokeObjectURL(url);
  };

  const cleanFilters = () => {
    setFecha("");
    setCuo("");
    setCuenta("");
    setOrigen("");
  };

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="border-0 shadow-sm">{error}</Alert>}

      {/* Tarjeta de Filtros Avanzados */}
      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <div className="d-flex align-items-center mb-3">
            <FaBookOpen className="text-secondary me-2" size={20} />
            <h5 className="mb-0 fw-semibold">Filtros del Libro Diario</h5>
          </div>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-muted">Período</Form.Label>
                <Form.Control type="month" className="fw-bold" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-muted">Fecha Exacta</Form.Label>
                <Form.Control type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-muted">CUO</Form.Label>
                <Form.Control type="text" placeholder="Buscar CUO..." value={cuo} onChange={(e) => setCuo(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-muted">Cuenta Contable</Form.Label>
                <Form.Control type="text" placeholder="Ej: 121201" value={cuenta} onChange={(e) => setCuenta(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-muted">Módulo Origen</Form.Label>
                <Form.Select value={origen} onChange={(e) => setOrigen(e.target.value)}>
                  <option value="">Todos los orígenes</option>
                  <option value="VENTA">Ventas</option>
                  <option value="COMPRA">Compras</option>
                  <option value="TESORERIA">Tesorería</option>
                  <option value="PLANILLA">Planillas</option>
                  <option value="NOTA_CREDITO">Notas de Crédito</option>
                  <option value="NOTA_CREDITO_COMPRA">Notas Crédito Compra</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <div className="d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" className="rounded-pill px-3 fw-bold btn-sm" onClick={cleanFilters}>Limpiar Filtros</Button>
            <Button variant="outline-success" className="rounded-pill px-3 fw-bold btn-sm" onClick={exportarExcel} disabled={loading}><FaFileExcel /> Excel</Button>
            <Button className="rounded-pill px-3 border-0 btn-sm" style={BTN} onClick={exportarTxtPle} disabled={loading}><FaDownload /> PLE Diario (5.1)</Button>
          </div>
        </Card.Body>
      </Card>

      {/* Lista de Asientos */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
        <div className="table-responsive">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="secondary" /><p className="mt-3 text-muted">Consultando gco_asientos...</p></div>
          ) : filteredAsientos.length === 0 ? (
            <div className="text-center py-5">
              <FaBookOpen size="4rem" className="text-muted opacity-25 mb-3" />
              <h5 className="text-muted fw-semibold">No se encontraron asientos con los criterios especificados</h5>
            </div>
          ) : (
            <Table hover className="align-middle bg-white mb-0" style={{ fontSize: "0.9rem" }}>
              <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <tr>
                  <th className="py-3 px-3 text-secondary fw-semibold">Fecha / Periodo</th>
                  <th className="py-3 px-3 text-secondary fw-semibold">CUO</th>
                  <th className="py-3 px-3 text-secondary fw-semibold">Glosa / Concepto</th>
                  <th className="py-3 px-3 text-secondary fw-semibold">Cuentas Debe</th>
                  <th className="py-3 px-3 text-secondary fw-semibold text-end">Debe</th>
                  <th className="py-3 px-3 text-secondary fw-semibold">Cuentas Haber</th>
                  <th className="py-3 px-3 text-secondary fw-semibold text-end">Haber</th>
                  <th className="py-3 px-3 text-secondary fw-semibold text-center">Origen</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((a) => (
                  <tr key={a.id} className="cont-row-hover" onClick={() => onSelectCuo && onSelectCuo(a.cuo || a.id)}>
                    <td className="px-3 py-3">
                      <div className="fw-medium text-dark">{a.fecha}</div>
                      <div className="small text-muted">{periodo}</div>
                    </td>
                    <td className="px-3 py-3 fw-bold text-primary">{a.cuo || "—"}</td>
                    <td className="px-3 py-3 text-muted" style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.glosa}
                    </td>
                    <td className="px-3 py-3">
                      {(a.debe || []).map((d, i) => (
                        <div key={i} className="small"><strong>{d.cuenta}</strong> - {d.descripcion}</div>
                      ))}
                    </td>
                    <td className="px-3 py-3 text-end fw-semibold text-success">
                      {(a.debe || []).map((d, i) => (
                        <div key={i}>{toNum(d.monto).toFixed(2)}</div>
                      ))}
                    </td>
                    <td className="px-3 py-3">
                      {(a.haber || []).map((h, i) => (
                        <div key={i} className="small"><strong>{h.cuenta}</strong> - {h.descripcion}</div>
                      ))}
                    </td>
                    <td className="px-3 py-3 text-end fw-semibold text-danger">
                      {(a.haber || []).map((h, i) => (
                        <div key={i}>{toNum(h.monto).toFixed(2)}</div>
                      ))}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <Badge bg="secondary" className="px-2 py-1 small rounded-pill">
                        {a.origen || "GENÉRICO"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
        {totalPages > 1 && (
          <div className="d-flex justify-content-center my-3">
            <Pagination>
              <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
              <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
              <Pagination.Item active>{currentPage}</Pagination.Item>
              <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} />
              <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>
          </div>
        )}
      </Card>
    </div>
  );
};

export default LibroDiario;
